<!--
A date picker on which all vee validation rules can be applied throught the "rules" prop.
See vee-validationrules.ts for list of available rules

Example use : 
<DatePickerWithValidation
  rule="required|date_is_after:begin-date"
  :tooltip="$t('input-date-end-tooltip')"
  :label="$t('input-date-end')"
  :placeHolder="$t('input-date-end')"
  v-model="endDate"
/>
-->
<template>
  <ValidationProvider
    v-slot="{ errors, valid, handleChange, handleBlur }"
    :vid="(group != '' ? group + '_' : '') + vid"
    :name="($attrs.name as string) || ($attrs.label as string)"
    :rules="rules"
    :model-value="innerValue ? innerValue.toISOString().substring(0, 10) : ''"
    @update:model-value="updateValue"
  >
    <b-field
      :type="{
        'is-normal': disabled,
        'is-danger': errors[0],
        'is-success': valid,
      }"
      :message="errors"
      class="DatePickerWithValidation-field"
    >
      <template #label>
        <FormInputLabel
          :validation-activated="validationActivated"
          :show-validation-errors="showValidationErrors"
          :label="($attrs.label as string) || ''"
          :is-mandatory="isMandatory"
          :disabled="disabled"
          :tooltip="tooltip"
        ></FormInputLabel>
      </template>

      <b-datepicker
        v-model="innerValue"
        show-week-number
        :placeholder="placeholder"
        locale="fr"
        icon="calendar-alt"
        trap-focus
        :disabled="disabled"
        :custom-class="disabled ? 'DatePickerWithValidation-disabled' : ''"
        :min-date="minDate"
        :max-date="maxDate"
        :unselectable-dates="unselectableDates"
        @update:model-value="
          (val: any) => {
            handleChange && handleChange(val)
          }
        "
        @blur="(event: any) => handleBlur && handleBlur(event)"
      >
      </b-datepicker>
    </b-field>
    <br v-if="!hideBr" />
    <br v-if="!hideBr" />
  </ValidationProvider>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import ValidationProvider from "@/services/validation/ValidationProvider.vue"
import FormInputLabel from "./FormInputLabel.vue"
import { BField, BDatepicker } from "buefy"

export default defineComponent({
  name: "DatePickerWithValidation",
  components: {
    ValidationProvider,
    FormInputLabel,
    BField,
    BDatepicker,
  },
  props: {
    group: {
      type: String,
      default: "",
    },
    vid: {
      type: String,
      default: "",
      required: false,
    },
    rules: {
      type: [String, Array],
      default: "",
      required: false,
    },
    modelValue: {
      type: [Date, String],
      default: "",
      required: false,
    },
    tooltip: {
      type: String,
      default: "",
      required: false,
    },
    placeholder: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hideBr: {
      type: Boolean,
      default: false,
    },
    minDate: {
      type: Date,
      default: null,
      required: false,
    },
    maxDate: {
      type: Date,
      default: null,
      required: false,
    },
    unselectableDates: {
      type: Array as () => Date[],
      default: () => [] as Date[],
      required: false,
    },
    validationActivated: {
      type: Boolean,
      default: false,
    },
    showValidationErrors: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["update:modelValue", "manual-change"],
  data() {
    return {
      innerValue: null as Date | null,
      isMandatory: false,
    }
  },
  watch: {
    innerValue(newVal: Date, _oldValue: Date): void {
      this.$emit("update:modelValue", newVal)
    },
    modelValue(newVal: Date, _oldValue: Date): void {
      this.innerValue = newVal
      if (newVal != _oldValue) {
        this.$emit("manual-change", newVal)
      }
    },
  },
  created(): void {
    if (this.modelValue) {
      this.innerValue =
        typeof this.modelValue === "string" ? new Date(this.modelValue) : this.modelValue
    }
    // Handle both string and array formats for rules
    if (this.rules) {
      if (Array.isArray(this.rules)) {
        this.isMandatory = this.rules.includes("required")
      } else {
        this.isMandatory = this.rules.includes("required")
      }
    } else {
      this.isMandatory = false
    }
  },
  methods: {
    updateValue(newValue: Date | null): void {
      this.innerValue = newValue
      // Émettre immédiatement pour synchroniser avec le parent
      this.$emit("update:modelValue", newValue)
    },
  },
})
</script>

<style lang="scss" scoped>
@import "@/styles/variables";

.DatePickerWithValidation-field {
  margin-bottom: $input-field-margin-bot;
}

.fieldLabel {
  display: flex;
  justify-content: space-between;
  width: 100%;

  .mandatory {
    padding-left: 20px;
    color: $dark;
  }
}

#app .DatePickerWithValidation-disabled {
  color: $text-default-color;
}
</style>
